import type { SubPageContent } from "@/components/common/SubPageLayout";
import type { Lang } from "@/lib/i18n";

export const COPY: Record<Lang, SubPageContent> = {
  en: {
    breadcrumbKey: "IndustrialWaterFiltration",
    relatedTopic: "water",
    hero: {
      categoryBadge: "Water & Liquid Filtration",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Stainless Steel Mesh for Industrial Water Filtration",
      lead: "Engineered woven wire cloth, sintered multilayer screen tubes, and heavy-duty strainer elements for industrial intake screening, cooling water loops, and recirculating process circuits.",
      secondaryCta: {
        label: "Water & Liquid Filtration",
        to: "/WaterLiquidFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-industrial-wa-459a67.png",
        alt: "Industrial stainless steel water filtration equipment and precision screen installation",
      },
      cardNote: "Rigid all-metal media & automated backwash support",
      cardLink: {
        label: "Technical Inquiry",
        to: "/ContactPage",
      },
      specs: [
        { label: "Aperture Range", value: "5 µm – 2,000 µm" },
        { label: "Alloy Grades", value: "AISI 304 / 316L" },
        { label: "Configurations", value: "Cylinders & Baskets" },
        { label: "Standards", value: "ASTM E2016 / ISO 9044" },
      ],
    },
    overview: {
      eyebrow: "Process Engineering",
      heading: "Reliable Particulate Retention for Plant Utilities & Cooling Water Circuits",
      paragraphs: [
        "Industrial manufacturing plants, power generating facilities, and processing complexes depend on continuous supplies of clean cooling and utility water. River and lake water intakes, open cooling tower circuits, and internal process loops carry airborne dust, pipe scale, algae, and suspended solids that can foul heat exchanger bundles, clog spray nozzles, and accelerate pump impeller wear.",
        "To protect downstream mechanical assets without interrupting hydraulic delivery, MutualFilter provides precision-fabricated stainless steel filter media. Our product line includes high-flow plain Dutch weave wire cloth, rigid diffusion-bonded multilayer sintered cylinders, and welded strainer baskets tailored for automatic backwash systems and simplex or duplex strainer housings.",
        "Manufactured strictly from AISI 304 and molybdenum-bearing AISI 316L stainless steel, these all-metal separation elements withstand oxidizing biocides, scale inhibitors, and chloride concentrations commonly found in industrial water treatments, ensuring extended service life and reliable dimensional stability.",
      ],
      keyPoints: [
        "High open-area geometries sustain maximum water throughput while maintaining low clean pressure drop across continuous recirculating circuits",
        "Rigid welded or sintered multilayer assemblies withstand hydraulic surges and reverse backwash pulses without media migration",
        "Corrosion-resistant AISI 304 and 316L stainless steel endure cooling tower chemistries, biocide dosing, and chloride exposure",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-industrial-wa-459a67.png",
        alt: "Industrial water filtration equipment and precision screen installation",
      },
    },
    specTable: {
      heading: "Media Configurations for Industrial Water Applications",
      intro: "Representative metallic filter media configurations tailored for plant intake screening, cooling loop protection, and process water polishing.",
      columns: [
        { key: "service", label: "Application Stage", align: "left" },
        { key: "construction", label: "Media Construction", align: "left" },
        { key: "rating", label: "Nominal Aperture", align: "right" },
        { key: "alloys", label: "Standard Alloys", align: "right" },
        { key: "mechanism", label: "Cleaning Method", align: "right" },
      ],
      rows: [
        {
          service: "Intake Water & Coarse Screening",
          construction: "Heavy square weave wire cloth cylinder",
          rating: "500 µm – 2,000 µm",
          alloys: "AISI 304 / 316L",
          mechanism: "Manual wash / backflush",
        },
        {
          service: "Cooling Tower Basin Side-Stream",
          construction: "Sintered multilayer tube / wire mesh cylinder",
          rating: "50 µm – 250 µm",
          alloys: "AISI 304 / 316L",
          mechanism: "Automated backwash cycle",
        },
        {
          service: "Pump & Spray Nozzle Protection",
          construction: "Perforated-backed plain Dutch weave basket",
          rating: "25 µm – 150 µm",
          alloys: "AISI 316L",
          mechanism: "Differential pressure backwash",
        },
        {
          service: "High-Purity Process Water Polishing",
          construction: "Diffusion-bonded sintered wire mesh laminate",
          rating: "5 µm – 40 µm",
          alloys: "AISI 316L",
          mechanism: "Reverse liquid pulse / ultrasonic",
        },
      ],
      caption: "All aperture ratings, layer structures, and mechanical end fittings are engineered to order to match specific housing designs.",
    },
    features: {
      heading: "Key Operational & Manufacturing Advantages",
      intro: "Our industrial water filtration media combine hydraulic throughput, structural stability, and metallurgical resistance across continuous utility service.",
      items: [
        {
          title: "Low Clean Pressure Drop",
          text: "High open-area square and Dutch weave configurations minimize initial hydraulic resistance, reducing energy consumption across high-volume pumping systems.",
        },
        {
          title: "Rigid Structural Integrity",
          text: "Precision longitudinal seam welding and internal perforated support cores resist collapse and media flexing during sudden hydraulic pressure transients.",
        },
        {
          title: "Corrosion & Biocide Resistance",
          text: "Austenitic AISI 304 and molybdenum-bearing 316L alloys resist localized pitting from dissolved chlorides and continuous dosing of water treatment biocides.",
        },
        {
          title: "Cleanable & Regenerable",
          text: "Uniform metallic pore geometry enables complete particle release during automated backwash cycles, mechanical scraping, or offline ultrasonic cleaning.",
        },
      ],
    },
    related: {
      heading: "Related Water Filtration Applications & Products",
      intro: "Explore complementary liquid separation applications and precision metallic filter media manufactured for industrial water circuits.",
      cards: [
        {
          title: "Water & Liquid Filtration",
          text: "Comprehensive metallic filtration media for industrial water, cooling circuits, process straining, and municipal treatment systems.",
          to: "/WaterLiquidFiltrationPage",
          badge: "Parent Application",
          linkLabel: "View Application Overview",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/water_filtration_mesh.png",
            alt: "Water and liquid filtration overview showing stainless steel separation media",
          },
        },
        {
          title: "Wastewater Filtration",
          text: "Heavy-duty wire mesh and continuous-slot wedge wire screens engineered for effluent screening, flocs interception, and dewatering.",
          to: "/WastewaterFiltrationPage",
          badge: "Sibling Application",
          linkLabel: "Explore Wastewater",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wastewater_screening_equipment.png",
            alt: "Industrial wastewater screening equipment with stainless steel screen elements",
          },
        },
        {
          title: "Liquid Straining Filtration",
          text: "High-differential-pressure metallic strainer baskets, cylinders, and Dutch weave elements for chemical slurries and process fluids.",
          to: "/LiquidFiltrationStrainingPage",
          badge: "Sibling Application",
          linkLabel: "Explore Liquid Straining",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-dutch-weave-st-40eb76.png",
            alt: "Clean macro photograph of Dutch weave stainless steel wire mesh for liquid filtration",
          },
        },
        {
          title: "Filter Components",
          text: "Precision welded and sintered filter tubes, cylinder screens, and strainer baskets for automatic backwash industrial water systems.",
          to: "/FilterComponentsPage",
          badge: "Recommended Product",
          linkLabel: "Explore Filter Components",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-an-industria-3baee0.png",
            alt: "Stainless steel welded and sintered filter tubes and cylinder screen components",
          },
        },
        {
          title: "Wedge Wire Screens",
          text: "Non-clogging continuous V-wire slot tubes and flat panels providing high mechanical strength and rapid backwash recovery for water intake.",
          to: "/WedgeWireScreenPage",
          badge: "Recommended Product",
          linkLabel: "Explore Wedge Wire",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-an-industria-cf3b91.png",
            alt: "Continuous slot stainless steel wedge wire screen tube for water filtration",
          },
        },
      ],
    },
    cta: {
      id: "industrial-water-quote",
      eyebrow: "Engineering Consultation",
      heading: "Request a Custom Industrial Water Filter Specification",
      lead: "Provide your required micron rating or aperture, flow geometry, alloy grade, and operating housing dimensions for an engineering evaluation and quotation.",
      secondaryLabel: "Water & Liquid Filtration",
      secondaryTo: "/WaterLiquidFiltrationPage",
      note: "Material test reports (MTR) and positive material identification (PMI) available when specified in the order.",
    },
  },
  zh: {
    breadcrumbKey: "IndustrialWaterFiltration",
    relatedTopic: "water",
    hero: {
      categoryBadge: "水与液体过滤",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "工业水过滤不锈钢丝网与精密滤网组件",
      lead: "专为工业原水取水截留、循环冷却水管网、换热器保护与工艺用水循环设计的精密编织丝网、多层烧结滤筒与重载滤篮构件。",
      secondaryCta: {
        label: "水与液体过滤总览",
        to: "/WaterLiquidFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-industrial-wa-459a67.png",
        alt: "工业不锈钢水过滤设备与精密反冲洗滤网组件安装展示",
      },
      cardNote: "全金属刚性结构与全自动反洗适配",
      cardLink: {
        label: "技术方案咨询",
        to: "/ContactPage",
      },
      specs: [
        { label: "拦截精度范围", value: "5 µm – 2,000 µm" },
        { label: "常用合金材质", value: "AISI 304 / 316L" },
        { label: "构型与形式", value: "圆筒滤筒 / 滤篮" },
        { label: "执行标准", value: "ASTM E2016 / ISO 9044" },
      ],
    },
    overview: {
      eyebrow: "工艺工程解析",
      heading: "工业公用工程与冷却水循环系统的可靠颗粒拦截",
      paragraphs: [
        "大型工业制造基地、电站机组及化工设施的稳定运行离不开持续洁净的公用工程用水和冷却水。江河湖泊取水、开式冷却塔循环水以及生产回用水系统中常夹带泥沙、铁锈、悬浮絮体与藻类，极易造成换热器管束结垢堵塞、喷嘴磨损以及泵体叶轮损坏。",
        "为在不影响系统供水通量的前提下可靠保护下游关键设备，MutualFilter 生产工程级不锈钢过滤介质。产品包括高通量密纹荷兰编织网、扩散焊接多层烧结复合圆筒以及全金属焊接滤篮，精准适配各类全自动反冲洗过滤器、自清洗刮刀系统与单双联篮式过滤器。",
        "构件采用耐腐蚀 AISI 304 及含钼 AISI 316L 不锈钢精工制造，稳定耐受工业水处理中常见的氧化性杀菌剂、缓蚀阻垢剂与氯离子侵蚀，长期保持精确的网孔几何形状与结构抗压刚性。",
      ],
      keyPoints: [
        "高开孔率编织网孔设计，确保在大流量循环水工况下维持极低初始压降，降低系统运行功耗",
        "刚性全焊接与多层烧结复合骨架，能够承受频繁的水锤波动与反冲洗反向脉冲载荷",
        "耐腐蚀 AISI 304 与 316L 材质，稳定耐受冷却塔水体中的氯离子、杀菌剂与化学药剂",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-industrial-wa-459a67.png",
        alt: "工业不锈钢水过滤设备与精密反冲洗滤网组件安装展示",
      },
    },
    specTable: {
      heading: "工业水过滤常见滤材配置参数",
      intro: "以下列出适用于原水取水预处理、冷却水旁流过滤以及工艺用水精滤的典型金属过滤介质配置。",
      columns: [
        { key: "service", label: "应用环节", align: "left" },
        { key: "construction", label: "核心构型与介质", align: "left" },
        { key: "rating", label: "名义截留精度", align: "right" },
        { key: "alloys", label: "推荐合金材质", align: "right" },
        { key: "mechanism", label: "清洗再生方式", align: "right" },
      ],
      rows: [
        {
          service: "原水进水取水粗滤",
          construction: "重载方孔编织网圆筒 / 骨架支撑筒",
          rating: "500 µm – 2,000 µm",
          alloys: "AISI 304 / 316L",
          mechanism: "人工冲洗 / 反向水流冲洗",
        },
        {
          service: "冷却塔集水池旁流循环",
          construction: "多层烧结网管 / 密纹编织滤筒",
          rating: "50 µm – 250 µm",
          alloys: "AISI 304 / 316L",
          mechanism: "全自动反冲洗程序",
        },
        {
          service: "泵吸入口与精密喷嘴保护",
          construction: "内衬冲孔骨架平纹荷兰网滤篮",
          rating: "25 µm – 150 µm",
          alloys: "AISI 316L",
          mechanism: "压差触发反洗 / 滤篮更换清洗",
        },
        {
          service: "高纯工艺水终端精滤",
          construction: "扩散焊接多层金属烧结网复合板/管",
          rating: "5 µm – 40 µm",
          alloys: "AISI 316L",
          mechanism: "反向液体脉冲 / 超声波清洗",
        },
      ],
      caption: "各项截留精度、层数结构、外形尺寸及接口法兰均可根据客户现有过滤器外壳定制加工。",
    },
    features: {
      heading: "关键运行与制造技术优势",
      intro: "我们的不锈钢工业水过滤介质兼具充沛的水力通量、结构抗压刚性与可靠的耐腐蚀冶金质量。",
      items: [
        {
          title: "极低初始水力压降",
          text: "经过精确计算的高开孔率编织孔隙最大程度降低流动阻力，有效减少大通量循环泵组的管网阻力损耗。",
        },
        {
          title: "坚固的整体焊接骨架",
          text: "精密纵缝焊接工艺与内置厚壁冲孔支撑芯管相结合，在突发水锤冲击与高压差工况下不易失稳或凹陷变形。",
        },
        {
          title: "出色的耐氯离子与化学侵蚀",
          text: "选用优质 AISI 304 及含钼 316L 奥氏体不锈钢，有效抵御水中氯化物点蚀、药剂氧化及常温酸碱水体侵蚀。",
        },
        {
          title: "高效反洗再生性能",
          text: "规则均一的金属微孔通道使截留杂质在自动反冲洗、机械刮刷或离线超声波清洗中迅速脱附，快速恢复初始通量。",
        },
      ],
    },
    related: {
      heading: "相关水处理应用与推荐产品",
      intro: "浏览 MutualFilter 针对工业用水系统打造的互补应用方案及精密金属过滤产品。",
      cards: [
        {
          title: "水与液体过滤",
          text: "涵盖工艺冷却水、原水取水拦截、液体精细筛滤及工业废液分离的全金属介质综合方案。",
          to: "/WaterLiquidFiltrationPage",
          badge: "父级应用分类",
          linkLabel: "查看应用总览",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/water_filtration_mesh.png",
            alt: "水与液体过滤总览及不锈钢过滤材料",
          },
        },
        {
          title: "废水过滤",
          text: "重载编织网与连续缝隙楔形丝筛网，适用于工业废水原水初筛、高浊度悬浮物截留与污泥脱水。",
          to: "/WastewaterFiltrationPage",
          badge: "同级应用",
          linkLabel: "探索废水过滤",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wastewater_screening_equipment.png",
            alt: "工业污水筛分设备与不锈钢筛网组件",
          },
        },
        {
          title: "液体过滤与筛滤",
          text: "高耐压差不锈钢编织丝网、滤篮与荷兰编织滤芯，用于流体澄清及高固含浆料拦截。",
          to: "/LiquidFiltrationStrainingPage",
          badge: "同级应用",
          linkLabel: "探索液体筛滤",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-dutch-weave-st-40eb76.png",
            alt: "用于液体过滤的不锈钢密纹编织金属丝网微观截留结构",
          },
        },
        {
          title: "过滤组件",
          text: "定制焊接与烧结圆筒滤筒、自清洗过滤器内网与滤篮组件，匹配各类工业自清洗水处理设备。",
          to: "/FilterComponentsPage",
          badge: "推荐产品",
          linkLabel: "探索过滤组件",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-an-industria-3baee0.png",
            alt: "不锈钢焊接与烧结圆筒滤筒滤网构件",
          },
        },
        {
          title: "楔形丝筛网",
          text: "连续缝隙 V 型绕丝滤筒与平板筛网，具备抗堵塞、高抗压强度与极佳的原水反洗复原性能。",
          to: "/WedgeWireScreenPage",
          badge: "推荐产品",
          linkLabel: "探索楔形丝网",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-an-industria-cf3b91.png",
            alt: "水过滤用连续缝隙不锈钢楔形丝筛管",
          },
        },
      ],
    },
    cta: {
      id: "industrial-water-quote",
      eyebrow: "定制方案咨询",
      heading: "获取工业水过滤器丝网与滤芯技术报价",
      lead: "请提供您的目标精度、水流通量要求、外形尺寸、材质牌号及连接方式，我们的工程团队将为您提供针对性的选型与报价方案。",
      secondaryLabel: "返回水与液体过滤",
      secondaryTo: "/WaterLiquidFiltrationPage",
      note: "材质报告 (MTR) 与化学成分检测 (PMI) 可按订单要求提供。",
    },
  },
  es: {
    breadcrumbKey: "IndustrialWaterFiltration",
    relatedTopic: "water",
    hero: {
      categoryBadge: "Filtración de Agua y Líquidos",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Mallas de Acero Inoxidable para Filtración de Agua Industrial",
      lead: "Telas metálicas de precisión, tubos de criba sinterizados multicapa y cestas coladoras de alta resistencia para tomas de agua, circuitos de refrigeración y agua de proceso.",
      secondaryCta: {
        label: "Filtración de Agua y Líquidos",
        to: "/WaterLiquidFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-industrial-wa-459a67.png",
        alt: "Equipo industrial de filtración de agua en acero inoxidable e instalación de malla de precisión",
      },
      cardNote: "Estructura metálica rígida y apta para retrolavado automático",
      cardLink: {
        label: "Consulta Técnica",
        to: "/ContactPage",
      },
      specs: [
        { label: "Rango de Abertura", value: "5 µm – 2.000 µm" },
        { label: "Aleaciones", value: "AISI 304 / 316L" },
        { label: "Configuraciones", value: "Cilindros y Cestas" },
        { label: "Normas", value: "ASTM E2016 / ISO 9044" },
      ],
    },
    overview: {
      eyebrow: "Ingeniería de Procesos",
      heading: "Retención Fiable de Partículas en Circuitos de Refrigeración y Servicios Industriales",
      paragraphs: [
        "Las plantas de producción industrial, las centrales energéticas y los complejos de proceso requieren suministros ininterrumpidos de agua de refrigeración y servicios auxiliares. Las tomas de agua superficial, las torres de refrigeración abiertas y los circuitos recirculantes arrastran polvo, óxidos de tubería y partículas que dañan los haces de intercambiadores, obstruyen boquillas y desgastan impulsores.",
        "Para proteger estos equipos mecánicos sin restringir el flujo hidráulico, MutualFilter suministra medios filtrantes de acero inoxidable diseñados con precisión. La gama abarca tejidos Dutch de alto caudal, tubos cilíndricos sinterizados multicapa y cestas coladoras soldadas compatibles con sistemas automáticos de retrolavado y filtros simples o dúplex.",
        "Fabricados en acero inoxidable AISI 304 y AISI 316L con molibdeno, estos elementos soportan biocidas oxidantes, inhibidores de incrustaciones y cloruros presentes en los tratamientos de agua industrial, asegurando una vida útil prolongada y estabilidad dimensional.",
      ],
      keyPoints: [
        "Geometrías de gran área abierta que mantienen el caudal de agua con mínima pérdida de carga en circuitos de recirculación continua",
        "Estructuras rígidas soldadas o sinterizadas que resisten pulsos de retrolavado y sobrepresiones hidráulicas sin desplazamiento de malla",
        "Aceros inoxidables AISI 304 y 316L resistentes a los químicos de torres de enfriamiento y a la dosificación de biocidas",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-industrial-wa-459a67.png",
        alt: "Equipo industrial de filtración de agua en acero inoxidable e instalación de malla de precisión",
      },
    },
    specTable: {
      heading: "Configuraciones de Medios para Agua Industrial",
      intro: "Medios filtrantes metálicos representativos para captación de agua bruta, protección de circuitos de refrigeración y afinado de agua de proceso.",
      columns: [
        { key: "service", label: "Etapa de Aplicación", align: "left" },
        { key: "construction", label: "Tipo de Construcción", align: "left" },
        { key: "rating", label: "Abertura Nominal", align: "right" },
        { key: "alloys", label: "Aleaciones Típicas", align: "right" },
        { key: "mechanism", label: "Método de Limpieza", align: "right" },
      ],
      rows: [
        {
          service: "Captación y Desbaste Grueso",
          construction: "Cilindro de tela metálica cuadrada reforzada",
          rating: "500 µm – 2.000 µm",
          alloys: "AISI 304 / 316L",
          mechanism: "Lavado manual / lavado a contracorriente",
        },
        {
          service: "Flujo Lateral de Torre de Enfriamiento",
          construction: "Tubo sinterizado multicapa / cilindro de malla",
          rating: "50 µm – 250 µm",
          alloys: "AISI 304 / 316L",
          mechanism: "Ciclo de retrolavado automático",
        },
        {
          service: "Protección de Bombas y Boquillas",
          construction: "Cesta coladora en tejido Dutch con soporte perforado",
          rating: "25 µm – 150 µm",
          alloys: "AISI 316L",
          mechanism: "Retrolavado por diferencial de presión",
        },
        {
          service: "Afinado de Agua de Proceso",
          construction: "Laminado sinterizado por difusión multicapa",
          rating: "5 µm – 40 µm",
          alloys: "AISI 316L",
          mechanism: "Pulso inverso / baño por ultrasonidos",
        },
      ],
      caption: "Todas las aberturas, capas y fijaciones mecánicas se adaptan a medida según el diseño de cada carcasa.",
    },
    features: {
      heading: "Ventajas Operativas y de Fabricación",
      intro: "Nuestros medios filtrantes para agua industrial conjugan capacidad hidráulica, rigidez estructural y resistencia a la corrosión.",
      items: [
        {
          title: "Baja Pérdida de Carga Inicial",
          text: "La alta superficie abierta minimiza la resistencia inicial al paso del fluido, reduciendo el consumo energético de las estaciones de bombeo.",
        },
        {
          title: "Rigidez Estructural Mecánica",
          text: "Soldaduras longitudinales de precisión y núcleos perforados interiores evitan el colapso durante transitorios hidráulicos y sobrepresiones.",
        },
        {
          title: "Resistencia a Biocidas y Corrosión",
          text: "Aleaciones austeníticas AISI 304 y 316L con molibdeno que previenen la corrosión por picaduras debida a cloruros y biocidas oxidantes.",
        },
        {
          title: "Limpiable y Regenerable",
          text: "La geometría regular de los poros metálicos facilita el desprendimiento de sólidos en ciclos automáticos de retrolavado o limpieza ultrasónica.",
        },
      ],
    },
    related: {
      heading: "Aplicaciones y Productos Relacionados",
      intro: "Explore soluciones complementarias en filtración de agua y medios metálicos de precisión diseñados por MutualFilter.",
      cards: [
        {
          title: "Filtración de Agua y Líquidos",
          text: "Soluciones completas de medios metálicos para circuitos industriales, torres de enfriamiento y tratamiento de efluentes.",
          to: "/WaterLiquidFiltrationPage",
          badge: "Aplicación Principal",
          linkLabel: "Ver Visión General",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/water_filtration_mesh.png",
            alt: "Visión general de filtración de agua y medios metálicos",
          },
        },
        {
          title: "Filtración de Aguas Residuales",
          text: "Mallas de alambre y cribas de alambre en cuña para separación de efluentes de alta turbidez y deshidratación de lodos.",
          to: "/WastewaterFiltrationPage",
          badge: "Aplicación Hermana",
          linkLabel: "Explorar Aguas Residuales",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wastewater_screening_equipment.png",
            alt: "Equipo industrial de aguas residuales con mallas de acero inoxidable",
          },
        },
        {
          title: "Filtración y Colado de Líquidos",
          text: "Cestas coladoras y elementos tubulares en tejido Dutch diseñados para retención de sólidos en suspensiones líquidas.",
          to: "/LiquidFiltrationStrainingPage",
          badge: "Aplicación Hermana",
          linkLabel: "Explorar Colado de Líquidos",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-dutch-weave-st-40eb76.png",
            alt: "Fotografía de tejido Dutch de acero inoxidable para filtración de líquidos",
          },
        },
        {
          title: "Componentes Filtrantes",
          text: "Tubos soldados, cilindros sinterizados y cestas filtrantes fabricados a medida para carcasas de retrolavado automático.",
          to: "/FilterComponentsPage",
          badge: "Producto Recomendado",
          linkLabel: "Explorar Componentes",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-an-industria-3baee0.png",
            alt: "Tubos y cilindros filtrantes de acero inoxidable soldados y sinterizados",
          },
        },
        {
          title: "Cribas de Alambre en Cuña",
          text: "Tubos y paneles continuos en V de alta rigidez y recuperación óptima por retrolavado para tomas de agua y desbaste.",
          to: "/WedgeWireScreenPage",
          badge: "Producto Recomendado",
          linkLabel: "Explorar Alambre en Cuña",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-an-industria-cf3b91.png",
            alt: "Tubo de criba de alambre en cuña para aguas residuales y filtración de agua",
          },
        },
      ],
    },
    cta: {
      id: "industrial-water-quote",
      eyebrow: "Consulta Técnica",
      heading: "Solicite una Especificación a Medida para Filtración de Agua Industrial",
      lead: "Indique la abertura nominal requerida, dimensiones de la carcasa, aleación de acero inoxidable y tipo de flujo para una revisión técnica y cotización.",
      secondaryLabel: "Volver a Filtración de Agua",
      secondaryTo: "/WaterLiquidFiltrationPage",
      note: "Informes de ensayo de material (MTR) y análisis PMI disponibles cuando se especifiquen en el pedido.",
    },
  },
  de: {
    breadcrumbKey: "IndustrialWaterFiltration",
    relatedTopic: "water",
    hero: {
      categoryBadge: "Wasser- & Flüssigkeitsfiltration",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Edelstahldrahtgewebe für die Industrielle Wasserfiltration",
      lead: "Präzisions-Drahtgewebe, mehrlagige Sinterfilterrohre und hochbelastbare Siebkörbe für industrielle Wasserentnahme, Kühlturmkreisläufe und Prozesswasserkreisläufe.",
      secondaryCta: {
        label: "Wasser- & Flüssigkeitsfiltration",
        to: "/WaterLiquidFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-industrial-wa-459a67.png",
        alt: "Industrielle Edelstahl-Wasserfiltrationsausrüstung und Präzisionssieb-Installation",
      },
      cardNote: "Ganzmetall-Stabilität & Eignung für automatische Rückspülung",
      cardLink: {
        label: "Technische Beratung",
        to: "/ContactPage",
      },
      specs: [
        { label: "Maschenweitenbereich", value: "5 µm – 2.000 µm" },
        { label: "Werkstoffe", value: "AISI 304 / 316L" },
        { label: "Konfigurationen", value: "Zylinder & Körbe" },
        { label: "Normen", value: "ASTM E2016 / ISO 9044" },
      ],
    },
    overview: {
      eyebrow: "Verfahrenstechnik",
      heading: "Zuverlässiger Partikelrückhalt für industrielle Versorgungssysteme & Kühlwasserkreisläufe",
      paragraphs: [
        "Industrielle Produktionsstandorte, Kraftwerksanlagen und verfahrenstechnische Komplexe sind auf eine kontinuierliche Bereitstellung von Kühl- und Betriebswasser angewiesen. Entnahmen aus Flüssen und Seen, offene Kühltürme und rezirkulierende Kreisläufe transportieren Sand, Rohrzunder, Algen und Schwebstoffe, die Wärmetauscherrohre verstopfen, Sprühdüsen zusetzen und Pumpenlaufräder abrasiv abnutzen.",
        "Um nachgeschaltete Aggregate ohne Unterbrechung der Wasserversorgung zu schützen, fertigt MutualFilter präzise metallische Filtermedien. Das Sortiment umfasst durchsatzstarke Tressengewebe, diffusionsgebundene mehrlagige Sinterzylinder und geschweißte Siebkörbe, abgestimmt auf automatische Rückspülfilter sowie Simplex- und Duplex-Gehäuse.",
        "Gefertigt aus korrosionsbeständigem Edelstahl AISI 304 und molybdänhaltigem AISI 316L widerstehen diese Ganzmetallelemente oxidierenden Bioziden, Härtehemmern und Chloriden, die in der industriellen Wasseraufbereitung auftreten, und sichern eine lange Lebensdauer bei hoher Maßhaltigkeit.",
      ],
      keyPoints: [
        "Gewebe mit hoher offener Siebfläche sichern maximalen Durchsatz bei minimalem Druckverlust in kontinuierlichen Kühlkreisläufen",
        "Formstabile geschweißte oder gesinterte Mehrlagenbaugruppen halten Druckstößen und Rückspülpulsen zuverlässig stand",
        "Korrosionsbeständige Legierungen AISI 304 und 316L widerstehen Kühlturmchemikalien, Chloriden und Bioziden",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-industrial-wa-459a67.png",
        alt: "Industrielle Edelstahl-Wasserfiltrationsausrüstung und Präzisionssieb-Installation",
      },
    },
    specTable: {
      heading: "Medienkonfigurationen für Industriewasseranwendungen",
      intro: "Repräsentative Filtermedien-Aufbauten für Rohwasserentnahme, Kühlturmpolierung und Prozesswasserschutz.",
      columns: [
        { key: "service", label: "Verfahrensstufe", align: "left" },
        { key: "construction", label: "Filtermedium-Aufbau", align: "left" },
        { key: "rating", label: "Nennmaschenweite", align: "right" },
        { key: "alloys", label: "Standard-Werkstoffe", align: "right" },
        { key: "mechanism", label: "Reinigungsmethode", align: "right" },
      ],
      rows: [
        {
          service: "Wasserentnahme & Grobsiebung",
          construction: "Schwerer Quadratmaschen-Gewebezylinder",
          rating: "500 µm – 2.000 µm",
          alloys: "AISI 304 / 316L",
          mechanism: "Manuelle Spülung / Rückspülung",
        },
        {
          service: "Kühlturmbecken-Teilstromfiltration",
          construction: "Mehrlagen-Sinterrohr / Tressenzylinder",
          rating: "50 µm – 250 µm",
          alloys: "AISI 304 / 316L",
          mechanism: "Automatischer Rückspülzyklus",
        },
        {
          service: "Pumpen- & Düsenschutz",
          construction: "Tressengewebe-Korb mit Stützlochrohr",
          rating: "25 µm – 150 µm",
          alloys: "AISI 316L",
          mechanism: "Differenzdruck-Rückspülung",
        },
        {
          service: "Feinpolierung von Prozesswasser",
          construction: "Diffusionsgebundenes Sintergewebelaminat",
          rating: "5 µm – 40 µm",
          alloys: "AISI 316L",
          mechanism: "Umkehrspülung / Ultraschallbad",
        },
      ],
      caption: "Sämtliche Feinheiten, Lagenaufbauten und Endanschlüsse werden auftragsbezogen an das vorhandene Gehäuse angepasst.",
    },
    features: {
      heading: "Betriebliche & Fertigungstechnische Vorteile",
      intro: "Unsere Edelstahlmedien für die industrielle Wasserfiltration vereinen hydraulischen Durchfluss, Formstabilität und metallurgische Beständigkeit.",
      items: [
        {
          title: "Minimaler Anfangsdruckverlust",
          text: "Hohe offene Flächen reduzieren den Durchströmungswiderstand und entlasten die Pumpensysteme im Dauerbetrieb.",
        },
        {
          title: "Mechanische Stabilität",
          text: "Präzisions-Längsnahtschweißung und innere Lochblech-Stützkörper verhindern Verformungen bei Druckschlägen und Differenzdrücken.",
        },
        {
          title: "Beständigkeit gegen Biozide",
          text: "Austenitische Qualitäten AISI 304 und 316L mit Molybdänzusatz schützen vor Lochkorrosion durch Chloride und Wasseraufbereitungschemikalien.",
        },
        {
          title: "Hervorragend Regenerierbar",
          text: "Gleichmäßige Metallporenpfade erlauben das schnelle Ablösen von Partikeln bei automatischer Rückspülung oder Ultraschallreinigung.",
        },
      ],
    },
    related: {
      heading: "Verwandte Wasserfiltrations-Anwendungen & Produkte",
      intro: "Entdecken Sie weitere industrielle Wasseranwendungen und präzise Metallfiltermedien aus der Fertigung von MutualFilter.",
      cards: [
        {
          title: "Wasser- & Flüssigkeitsfiltration",
          text: "Ganzheitliche metallische Trennlösungen für Prozesswasser, Kühlsysteme und industrielle Abwasserreinigung.",
          to: "/WaterLiquidFiltrationPage",
          badge: "Hauptanwendung",
          linkLabel: "Übersicht ansehen",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/water_filtration_mesh.png",
            alt: "Wasser- und Flüssigkeitsfiltration Übersicht mit Edelstahl-Filtermedien",
          },
        },
        {
          title: "Abwasserfiltration",
          text: "Hochbelastbare Drahtgewebe und Spaltsiebe für Primärsiebung, Flockenabscheidung und Schlammentwässerung.",
          to: "/WastewaterFiltrationPage",
          badge: "Geschwisteranwendung",
          linkLabel: "Abwasserfiltration ansehen",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wastewater_screening_equipment.png",
            alt: "Industrielle Entwässerungs- und Trommelsiebanlage mit Edelstahl-Filtergewebe",
          },
        },
        {
          title: "Flüssigkeitsfiltration & Siebung",
          text: "Druckfeste Siebkörbe, Zylinder und Tressengewebe für anspruchsvolle Flüssigkeitsklärung und Schlammtrennung.",
          to: "/LiquidFiltrationStrainingPage",
          badge: "Geschwisteranwendung",
          linkLabel: "Flüssigkeitssiebung ansehen",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-dutch-weave-st-40eb76.png",
            alt: "Edelstahl-Tressengewebe für die Flüssigkeitsfiltration",
          },
        },
        {
          title: "Filterkomponenten",
          text: "Geschweißte und gesinterte Filterrohre, Zylinder und Siebkörbe für automatische Rückspülfiltergehäuse.",
          to: "/FilterComponentsPage",
          badge: "Empfohlenes Produkt",
          linkLabel: "Filterkomponenten entdecken",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-an-industria-3baee0.png",
            alt: "Geschweißte und gesinterte Filterrohre aus Edelstahl",
          },
        },
        {
          title: "Spaltsiebe",
          text: "Verstopfungsfreie V-Draht-Flachpaneele und Zylinderrohre für hohe mechanische Festigkeit und Rückspülbarkeit.",
          to: "/WedgeWireScreenPage",
          badge: "Empfohlenes Produkt",
          linkLabel: "Spaltsiebe entdecken",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-an-industria-cf3b91.png",
            alt: "Spaltsiebrohr aus Edelstahl für Wasser- und Abwasserfiltration",
          },
        },
      ],
    },
    cta: {
      id: "industrial-water-quote",
      eyebrow: "Technische Anfrage",
      heading: "Angebot für Industriewasser-Filterelemente anfordern",
      lead: "Nennen Sie die gewünschte Maschenweite, Gehäuseabmessungen, Werkstoffauswahl und Durchflussanforderungen für eine technische Auslegung.",
      secondaryLabel: "Zurück zu Wasserfiltration",
      secondaryTo: "/WaterLiquidFiltrationPage",
      note: "Werksprüfzeugnisse (MTR) und PMI-Werkstoffanalysen auf Anfrage gemäß Auftragsvereinbarung verfügbar.",
    },
  },
  fr: {
    breadcrumbKey: "IndustrialWaterFiltration",
    relatedTopic: "water",
    hero: {
      categoryBadge: "Filtration d'Eau et de Liquides",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Toiles Métalliques en Inox pour la Filtration d'Eau Industrielle",
      lead: "Toiles métalliques de précision, tubes frittés multicouches et paniers filtrants renforcés pour prises d'eau industrielles, circuits de refroidissement et boucles de procédé.",
      secondaryCta: {
        label: "Filtration d'Eau et de Liquides",
        to: "/WaterLiquidFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-industrial-wa-459a67.png",
        alt: "Équipement industriel de filtration d'eau en acier inoxydable et tamis de précision",
      },
      cardNote: "Conception tout métal et compatibilité avec contre-lavage automatique",
      cardLink: {
        label: "Consultation Technique",
        to: "/ContactPage",
      },
      specs: [
        { label: "Plage d'Ouverture", value: "5 µm – 2 000 µm" },
        { label: "Nuances d'Alliages", value: "AISI 304 / 316L" },
        { label: "Configurations", value: "Cylindres et Paniers" },
        { label: "Normes", value: "ASTM E2016 / ISO 9044" },
      ],
    },
    overview: {
      eyebrow: "Génie des Procédés",
      heading: "Rétention Fiable des Particules pour Utilités Industrielles et Circuits de Refroidissement",
      paragraphs: [
        "Les installations industrielles, centrales énergétiques et complexes de transformation dépendent d'un approvisionnement continu en eau de refroidissement et de service. Les prises d'eau de surface, les tours aéroréfrigérantes et les circuits internes véhiculent des sables, des oxydes et des matières en suspension risquant d'encrasser les échangeurs, d'obstruer les buses et d'user prématurément les pompes.",
        "Afin de protéger ces équipements sans restreindre le débit hydraulique, MutualFilter conçoit des médias filtrants en acier inoxydable de haute précision. La gamme inclut des toiles à tissage hollandais grand débit, des cylindres frittés multicouches rigides et des paniers métalliques soudés adaptés aux filtres autonettoyants à contre-lavage.",
        "Fabriqués en inox AISI 304 et AISI 316L au molybdène, ces éléments résistent aux biocides oxydants, aux agents antitartre et aux chlorures couramment présents dans les eaux industrielles, garantissant une longévité élevée et une stabilité dimensionnelle irréprochable.",
      ],
      keyPoints: [
        "Tissages à grande surface ouverte maintenant un débit d'eau maximal avec une perte de charge minime en recirculation continue",
        "Ensembles rigides soudés ou frittés résistant aux coups de bélier et aux cycles de contre-lavage répétés sans déformation",
        "Nuances inoxydables AISI 304 et 316L résistant aux chlorures, aux biocides et aux produits chimiques des tours de refroidissement",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-industrial-wa-459a67.png",
        alt: "Équipement industriel de filtration d'eau en acier inoxydable et tamis de précision",
      },
    },
    specTable: {
      heading: "Configurations de Médias pour Eau Industrielle",
      intro: "Médias filtrants métalliques types pour dégrillage de prise d'eau, protection de boucles de refroidissement et clarification.",
      columns: [
        { key: "service", label: "Étape de Procédé", align: "left" },
        { key: "construction", label: "Type de Construction", align: "left" },
        { key: "rating", label: "Ouverture Nominale", align: "right" },
        { key: "alloys", label: "Alliages Standards", align: "right" },
        { key: "mechanism", label: "Mode de Nettoyage", align: "right" },
      ],
      rows: [
        {
          service: "Prise d'Eau & Dégrillage Primaire",
          construction: "Cylindre en toile métallique carrée lourde",
          rating: "500 µm – 2 000 µm",
          alloys: "AISI 304 / 316L",
          mechanism: "Rinçage manuel / contre-courant",
        },
        {
          service: "Boucle de Tour de Refroidissement",
          construction: "Tube fritté multicouche / cylindre tissé",
          rating: "50 µm – 250 µm",
          alloys: "AISI 304 / 316L",
          mechanism: "Cycle de contre-lavage automatique",
        },
        {
          service: "Protection de Pompes et Buses",
          construction: "Panier filtrant en toile hollandaise avec support",
          rating: "25 µm – 150 µm",
          alloys: "AISI 316L",
          mechanism: "Contre-lavage sur seuil de pression différentielle",
        },
        {
          service: "Polissage d'Eau de Procédé",
          construction: "Stratifié fritté par diffusion multicouche",
          rating: "5 µm – 40 µm",
          alloys: "AISI 316L",
          mechanism: "Impulsion liquide inverse / bain ultrasons",
        },
      ],
      caption: "Tous les seuils de coupure, structures de couches et raccords mécaniques sont fabriqués sur mesure selon les corps de filtre existants.",
    },
    features: {
      heading: "Atouts Fonctionnels et de Fabrication",
      intro: "Nos médias en inox pour eau industrielle associent débit hydraulique, rigidité mécanique et durabilité métallurgique.",
      items: [
        {
          title: "Faible Perte de Charge Initiale",
          text: "Les fortes ouvertures réduisent la résistance à l'écoulement et limitent la consommation énergétique des groupes de pompage.",
        },
        {
          title: "Rigidité Mécanique Élevée",
          text: "Soudage longitudinal précis et tubes perforés de renfort évitant tout écrasement lors des surpressions hydrauliques.",
        },
        {
          title: "Résistance Chimique et aux Biocides",
          text: "Nuances austénitiques 304 et 316L au molybdène empêchant la piqûration due aux chlorures et aux agents chlorés ou oxydants.",
        },
        {
          title: "Régénérable et Nettoyable",
          text: "La géométrie régulière des pores métalliques permet une évacuation complète des particules piégées lors des rétrolavages ou ultrasons.",
        },
      ],
    },
    related: {
      heading: "Applications et Produits Associés",
      intro: "Découvrez les solutions complémentaires de filtration d'eau et les médias métalliques conçus par MutualFilter.",
      cards: [
        {
          title: "Filtration d'Eau et de Liquides",
          text: "Solutions de séparation métallique pour eaux industrielles, circuits de refroidissement et traitement des effluents.",
          to: "/WaterLiquidFiltrationPage",
          badge: "Application Principale",
          linkLabel: "Voir la Présentation",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/water_filtration_mesh.png",
            alt: "Présentation générale de la filtration d'eau avec toiles métalliques",
          },
        },
        {
          title: "Filtration des Eaux Usées",
          text: "Toiles tissées robustes et grilles à fil profilé pour le dégrillage d'effluents, l'interception de flocs et l'égouttage de boues.",
          to: "/WastewaterFiltrationPage",
          badge: "Application Associée",
          linkLabel: "Explorer les Eaux Usées",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wastewater_screening_equipment.png",
            alt: "Équipement industriel d'eaux usées avec tamis en acier inoxydable",
          },
        },
        {
          title: "Filtration et Tamisage de Liquides",
          text: "Paniers filtrants, cylindres et toiles hollandaises pour la clarification de fluides et la rétention de suspensions.",
          to: "/LiquidFiltrationStrainingPage",
          badge: "Application Associée",
          linkLabel: "Explorer le Tamisage Liquide",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-dutch-weave-st-40eb76.png",
            alt: "Toile métallique en acier inoxydable pour filtration de liquides",
          },
        },
        {
          title: "Composants Filtrants",
          text: "Tubes soudés, cylindres frittés et paniers métalliques adaptés aux carters de filtres autonettoyants industriels.",
          to: "/FilterComponentsPage",
          badge: "Produit Recommandé",
          linkLabel: "Explorer les Composants",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-an-industria-3baee0.png",
            alt: "Tubes et cylindres filtrants en acier inoxydable soudés et frittés",
          },
        },
        {
          title: "Grilles à Fil Profilé",
          text: "Tubes cylindriques et panneaux continus à fil en V offrant une excellente rigidité et un décolmatage rapide pour prises d'eau.",
          to: "/WedgeWireScreenPage",
          badge: "Produit Recommandé",
          linkLabel: "Explorer le Fil Profilé",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-an-industria-cf3b91.png",
            alt: "Tube filtrant à fil profilé en inox pour eaux usées et eau",
          },
        },
      ],
    },
    cta: {
      id: "industrial-water-quote",
      eyebrow: "Étude Technique",
      heading: "Demandez un Devis pour Éléments Filtrants d'Eau Industrielle",
      lead: "Précisez votre seuil d'ouverture, les dimensions du carter existant, la nuance souhaitée et les contraintes d'installation pour une offre chiffrée.",
      secondaryLabel: "Retour à la Filtration d'Eau",
      secondaryTo: "/WaterLiquidFiltrationPage",
      note: "Certificats matière (MTR) et contrôles PMI disponibles sur demande selon spécifications de la commande.",
    },
  },
  pt: {
    breadcrumbKey: "IndustrialWaterFiltration",
    relatedTopic: "water",
    hero: {
      categoryBadge: "Filtração de Água e Líquidos",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Telas de Aço Inoxidável para Filtração de Água Industrial",
      lead: "Telas de arame de precisão, tubos sinterizados multicamadas e cestos filtrantes de alta resistência para captação de água, torres de resfriamento e circuitos de processo.",
      secondaryCta: {
        label: "Filtração de Água e Líquidos",
        to: "/WaterLiquidFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-industrial-wa-459a67.png",
        alt: "Equipamento industrial de filtração de água em aço inoxidável e tela de precisão",
      },
      cardNote: "Construção metálica rígida e suporte para retrolavagem automática",
      cardLink: {
        label: "Consulta Técnica",
        to: "/ContactPage",
      },
      specs: [
        { label: "Faixa de Abertura", value: "5 µm – 2.000 µm" },
        { label: "Ligas de Aço", value: "AISI 304 / 316L" },
        { label: "Configurações", value: "Cilindros e Cestos" },
        { label: "Normas", value: "ASTM E2016 / ISO 9044" },
      ],
    },
    overview: {
      eyebrow: "Engenharia de Processos",
      heading: "Retenção Confiável de Partículas em Circuitos de Resfriamento e Utilidades Industriais",
      paragraphs: [
        "Unidades industriais de manufatura, usinas termelétricas e complexos de processo necessitam de suprimento ininterrupto de água de resfriamento e utilidades. Captações superficiais em rios, bacias de torres de resfriamento abertas e circuitos fechados transportam poeiras, óxidos de tubulações e sólidos suspensos que incrustam trocadores de calor, obstruem bicos aspersores e causam abrasão em rotores de bombas.",
        "Para preservar esses ativos sem comprometer a vazão hidráulica, a MutualFilter fabrica meios filtrantes em aço inoxidável de alta precisão. A linha inclui telas holandesas de alta vazão, tubos cilíndricos sinterizados multicamadas e cestos soldados para filtros autolimpantes de retrolavagem e carcaças tipo cesto simples ou duplex.",
        "Fabricados exclusivamente em aço inoxidável AISI 304 e AISI 316L com molibdênio, estes componentes metálicos suportam biocidas oxidantes, dispersantes e cloretos presentes nas águas industriais, proporcionando longa durabilidade e estabilidade dimensional.",
      ],
      keyPoints: [
        "Telas de alta área aberta que mantêm vazão máxima de água com mínima perda de carga em circuitos de recirculação contínua",
        "Conjuntos rígidos soldados ou sinterizados que resistem a transientes de pressão e pulsos de retrolavagem sem deformação",
        "Ligas inoxidáveis AISI 304 e 316L resistentes a produtos químicos de torres de resfriamento e dosagem de biocidas",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-industrial-wa-459a67.png",
        alt: "Equipamento industrial de filtração de água em aço inoxidável e tela de precisão",
      },
    },
    specTable: {
      heading: "Configurações de Meios para Água Industrial",
      intro: "Meios filtrantes metálicos representativos para captação de água bruta, polimento de bacias de resfriamento e água de utilidades.",
      columns: [
        { key: "service", label: "Etapa de Aplicação", align: "left" },
        { key: "construction", label: "Estrutura do Meio Filtrante", align: "left" },
        { key: "rating", label: "Abertura Nominal", align: "right" },
        { key: "alloys", label: "Ligas Típicas", align: "right" },
        { key: "mechanism", label: "Método de Limpeza", align: "right" },
      ],
      rows: [
        {
          service: "Captação de Água Bruta e Gradeamento",
          construction: "Cilindro de tela quadrada tecida reforçada",
          rating: "500 µm – 2.000 µm",
          alloys: "AISI 304 / 316L",
          mechanism: "Lavagem manual / contra-fluxo",
        },
        {
          service: "Filtração Lateral de Torre de Resfriamento",
          construction: "Tubo sinterizado multicamadas / cilindro de tela",
          rating: "50 µm – 250 µm",
          alloys: "AISI 304 / 316L",
          mechanism: "Ciclo de retrolavagem automática",
        },
        {
          service: "Proteção de Bombas e Bicos Injetores",
          construction: "Cesto em trama holandesa com tubo perfurado de suporte",
          rating: "25 µm – 150 µm",
          alloys: "AISI 316L",
          mechanism: "Retrolavagem por diferencial de pressão",
        },
        {
          service: "Polimento de Água de Processo",
          construction: "Laminado sinterizado por difusão multicamadas",
          rating: "5 µm – 40 µm",
          alloys: "AISI 316L",
          mechanism: "Pulso líquido reverso / banho ultrassônico",
        },
      ],
      caption: "Todas as faixas de abertura, camadas e flanges de fixação são customizadas conforme o projeto das carcaças.",
    },
    features: {
      heading: "Destaques Operacionais e Construtivos",
      intro: "Nossos meios de filtração de água industrial aliam capacidade hidráulica, resistência mecânica e durabilidade metalúrgica.",
      items: [
        {
          title: "Baixa Perda de Carga Inicial",
          text: "A ampla área aberta minimiza o atrito hidráulico inicial, contribuindo para a eficiência energética do bombeamento.",
        },
        {
          title: "Integridade Estrutural e Mecânica",
          text: "Soldas longitudinais precisas e núcleos perfurados internos mantêm a forma sob golpes de aríete e sobrepressões operacionais.",
        },
        {
          title: "Resistência a Biocidas e Corrosão",
          text: "Aços austeníticos 304 e 316L com molibdênio previnem corrosão por pite provocada por cloretos e agentes químicos oxidantes.",
        },
        {
          title: "Limpabilidade e Regeneração",
          text: "A geometria dos poros metálicos permite a liberação imediata dos sólidos retidos durante a retrolavagem ou limpeza ultrassônica.",
        },
      ],
    },
    related: {
      heading: "Aplicações e Produtos Relacionados",
      intro: "Conheça soluções integradas de filtração de água e meios metálicos de precisão fabricados pela MutualFilter.",
      cards: [
        {
          title: "Filtração de Água e Líquidos",
          text: "Soluções completas de separação metálica para captação, refrigeração e tratamento de efluentes industriais.",
          to: "/WaterLiquidFiltrationPage",
          badge: "Aplicação Principal",
          linkLabel: "Ver Visão Geral",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/water_filtration_mesh.png",
            alt: "Visão geral de filtração de água e meios metálicos de separação",
          },
        },
        {
          title: "Filtração de Efluentes",
          text: "Telas tecidas de alta resistência e telas de arame em cunha com ranhura contínua projetadas para desaguamento e efluentes.",
          to: "/WastewaterFiltrationPage",
          badge: "Aplicação Irmã",
          linkLabel: "Explorar Efluentes",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wastewater_screening_equipment.png",
            alt: "Equipamento industrial de tratamento de efluentes com telas de aço inoxidável",
          },
        },
        {
          title: "Filtração e Retenção de Líquidos",
          text: "Cestos metálicos reforçados, tubos e telas holandesas para clarificação de fluidos e retenção em suspensões.",
          to: "/LiquidFiltrationStrainingPage",
          badge: "Aplicação Irmã",
          linkLabel: "Explorar Retenção de Líquidos",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-dutch-weave-st-40eb76.png",
            alt: "Malha de arame de aço inoxidável em trama holandesa para filtração de líquidos",
          },
        },
        {
          title: "Componentes Filtrantes",
          text: "Tubos soldados, cilindros sinterizados e cestos customizados para carcaças com retrolavagem automática.",
          to: "/FilterComponentsPage",
          badge: "Produto Recomendado",
          linkLabel: "Explorar Componentes",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-an-industria-3baee0.png",
            alt: "Tubos e cilindros filtrantes soldados e sinterizados em aço inoxidável",
          },
        },
        {
          title: "Telas de Arame em Cunha",
          text: "Tubos e painéis com ranhura contínua em V com excelente rigidez mecânica e rápida regeneração por retrolavagem.",
          to: "/WedgeWireScreenPage",
          badge: "Produto Recomendado",
          linkLabel: "Explorar Arame em Cunha",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-an-industria-cf3b91.png",
            alt: "Tubo de tela de arame cunha em aço inoxidável para filtração de água e efluentes",
          },
        },
      ],
    },
    cta: {
      id: "industrial-water-quote",
      eyebrow: "Consulta de Engenharia",
      heading: "Solicite uma Cotação para Elementos Filtrantes de Água Industrial",
      lead: "Informe a abertura nominal, dimensões da carcaça, liga de aço inoxidável e vazão operacional para análise técnica e orçamento.",
      secondaryLabel: "Voltar para Filtração de Água",
      secondaryTo: "/WaterLiquidFiltrationPage",
      note: "Relatórios de ensaio de material (MTR) e análise PMI disponíveis quando especificados no pedido.",
    },
  },
};
